import React, { useState, useEffect, useMemo } from 'react';
import PropTypes from 'prop-types';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Stack,
} from '@mui/material';

const REASONS = [
    { value: 'LOW_QUALITY', label: 'Foto com baixa qualidade' },
    { value: 'OFF_TOPIC', label: 'Fora do tema' },
    { value: 'DUPLICATE', label: 'Conteúdo duplicado' },
    { value: 'INSUFFICIENT_PROOF', label: 'Comprovação insuficiente' },
    { value: 'INAPPROPRIATE_CONTENT', label: 'Conteúdo impróprio' },
    { value: 'WRONG_USER', label: 'Usuário incorreto' },
    { value: 'WRONG_LOCATION_OR_TIME', label: 'Local/horário incorreto' },
    { value: 'SUSPICIOUS_ACTIVITY', label: 'Atividade suspeita' },
    { value: 'OTHER', label: 'Outro' },
];

export default function RejectReasonDialog({
    open,
    onClose,
    onConfirm,
    defaultReason,
}) {
    const [reason, setReason] = useState(defaultReason || 'INSUFFICIENT_PROOF');
    const [note, setNote] = useState('');

    useEffect(() => {
        if (open) {
            setReason(defaultReason || 'INSUFFICIENT_PROOF');
            setNote('');
        }
    }, [open, defaultReason]);

    const isOther = reason === 'OTHER';

    const isValid = useMemo(
        () => Boolean(reason) && (!isOther || note.trim().length > 0),
        [reason, isOther, note]
    );

    const handleSubmit = (e) => {
        e?.preventDefault();
        if (!isValid) return;
        onConfirm(reason, isOther ? note.trim() : undefined);
    };

    return (
        <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontFamily: 'Poppins' }}>
                Reprovar conteúdo
            </DialogTitle>
            <DialogContent component="form" onSubmit={handleSubmit}>
                <Stack spacing={2} sx={{ mt: 1 }}>
                    <FormControl fullWidth>
                        <InputLabel id="reject-reason-label">Motivo</InputLabel>
                        <Select
                            labelId="reject-reason-label"
                            label="Motivo"
                            value={reason}
                            onChange={(e) => setReason(e.target.value)}
                            fullWidth
                            required
                        >
                            {REASONS.map((r) => (
                                <MenuItem key={r.value} value={r.value}>
                                    {r.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>

                    {isOther && (
                        <TextField
                            label='Observação (obrigatória em "Outro")'
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            fullWidth
                            multiline
                            minRows={3}
                            inputProps={{ maxLength: 500 }}
                            required
                            autoFocus
                            error={isOther && note.trim().length === 0}
                            helperText={
                                isOther && note.trim().length === 0
                                    ? 'Descreva o motivo para "Outro".'
                                    : undefined
                            }
                        />
                    )}
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button variant="outlined" onClick={onClose}>
                    Cancelar
                </Button>
                <Button
                    variant="contained"
                    color="error"
                    onClick={handleSubmit}
                    disabled={!isValid}
                    type="submit"
                >
                    Confirmar reprovação
                </Button>
            </DialogActions>
        </Dialog>
    );
}

RejectReasonDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    onConfirm: PropTypes.func.isRequired,
    defaultReason: PropTypes.string,
};
